import { useCallback, useState } from 'react'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { ISearchedMotorCarrier } from 'apps/fleet-planner/components/carrier-search/types'
import { useUserContext } from 'apps/fleet-planner/context/UserContext'
import { useJoinMotorCarriersMutation } from './graphql/JoinMotorCarriersMutation'

export interface IJoinMotorCarriersProps {
  onError?: () => Promise<void> | void
  onSuccess?: () => Promise<void> | void
}

export type TJoinMotorCarriers = (carriers: ISearchedMotorCarrier[]) => Promise<void>

export interface IUseJoinMotorCarriersResult {
  errorMessages: string[]
  clearErrorMessages: () => void
  loading: boolean
  joinMotorCarriers: TJoinMotorCarriers
}

export const useJoinMotorCarriers = ({
  onError,
  onSuccess,
}: IJoinMotorCarriersProps = {}): IUseJoinMotorCarriersResult => {
  const isOnboardingCarrierMutationEnabled = useFeatureFlag('fmsOnboardingCarrierMutation')
  const [errorMessages, setErrorMessages] = useState<string[]>([])

  const {
    fetchUserData,
    isLoading: loadingRest,
    joinMotorCarriers: joinMotorCarriersRestRequest,
  } = useUserContext()
  const [joinMotorCarriersGqlRequest, { loading: loadingGql }] = useJoinMotorCarriersMutation()

  const joinMotorCarriersGql: TJoinMotorCarriers = useCallback(
    async carriers => {
      try {
        const { data, errors: mutationErrors } = await joinMotorCarriersGqlRequest({
          variables: {
            input: {
              motorCarriers: carriers.map(({ dotNumber, mcNumber }) => ({ dotNumber, mcNumber })),
            },
          },
        })
        const { joinMotorCarriers } = data ?? {}
        const { errors: serviceErrors } = joinMotorCarriers ?? {}

        if (mutationErrors?.length || serviceErrors?.length) {
          setErrorMessages([
            ...(mutationErrors?.map(err => err.message) ?? []),
            ...(serviceErrors?.map(err => err.message) ?? []),
          ])
          await onError?.()
        } else {
          await fetchUserData()
          await onSuccess?.()
        }
      } catch (e) {
        await onError?.()
      }
    },
    [fetchUserData, joinMotorCarriersGqlRequest, onError, onSuccess]
  )

  const joinMotorCarriersRest: TJoinMotorCarriers = useCallback(
    async carriers => {
      const success = await joinMotorCarriersRestRequest({
        motorCarriers: carriers.filter(({ id }) => id).map(({ id }) => ({ id: Number(id) })),
      })

      if (success) {
        await onSuccess?.()
      } else {
        await onError?.()
        setErrorMessages(['Something went wrong.'])
      }
    },
    [joinMotorCarriersRestRequest, onError, onSuccess]
  )

  const joinMotorCarriers: TJoinMotorCarriers = useCallback(
    async carriers => {
      if (isOnboardingCarrierMutationEnabled) {
        await joinMotorCarriersGql(carriers)
      } else {
        await joinMotorCarriersRest(carriers)
      }
    },
    [isOnboardingCarrierMutationEnabled, joinMotorCarriersGql, joinMotorCarriersRest]
  )

  const clearErrorMessages = useCallback(() => setErrorMessages([]), [])

  return {
    errorMessages,
    clearErrorMessages,
    loading: loadingGql || loadingRest,
    joinMotorCarriers,
  }
}
